import { 
  collection, 
  doc, 
  addDoc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { db } from './config';
import { auditLogService } from '@/services/auditLogService';

export interface Location {
  id?: string;
  name: string;
  address: string;
  city?: string;
  state: string;
  country: string;
  zipCode?: string;
  assignedTemplates: string[]; // Array of template IDs
  deleted?: boolean; // Soft delete flag
  organizationId?: string; // Multi-tenant support
  createdAt?: any;
  updatedAt?: any;
}

// Location service
export const locationService = {
  // Get all locations (excluding deleted ones) for an organization
  getAllLocations: async (organizationId?: string) => {
    try {
      const locationsRef = collection(db, 'locations');
      
      // Build query with organization filter if provided
      let q;
      if (organizationId) {
        // Filter by organizationId without orderBy to avoid index requirement
        q = query(
          locationsRef,
          where('organizationId', '==', organizationId)
        );
      } else {
        q = query(locationsRef, orderBy('name'));
      }
      
      const querySnapshot = await getDocs(q);
      const locations: Location[] = [];
      querySnapshot.forEach((doc) => {
        const locationData = { id: doc.id, ...doc.data() } as Location;
        // Filter out deleted locations
        if (!locationData.deleted) {
          locations.push(locationData);
        }
      });
      
      // Sort in memory after fetching to avoid index requirement
      if (organizationId) {
        locations.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      }
      
      return { success: true, data: locations };
    } catch (error: any) {
      console.error('Error getting all locations:', error);
      return { success: false, error: error.message };
    }
  },

  // Get user's accessible locations (filtered by assigned locations unless admin)
  getAccessibleLocations: async (organizationId?: string, userId?: string, userRole?: string, userLocationIds?: string[]) => {
    try {
      // First get all locations for organization
      const allLocationsResult = await locationService.getAllLocations(organizationId);
      if (!allLocationsResult.success || !allLocationsResult.data) {
        return allLocationsResult;
      }

      const allLocations = allLocationsResult.data;

      // Only admins see all locations
      if (userRole === 'admin') {
        return { success: true, data: allLocations };
      }

      // If no user ID provided, return empty (user not logged in)
      if (!userId) {
        return { success: true, data: [] };
      }

      // For managers and regular users, filter by assigned locationIds
      if (userLocationIds && userLocationIds.length > 0) {
        const accessibleLocations = allLocations.filter(loc => 
          loc.id && userLocationIds.includes(loc.id)
        );
        return { success: true, data: accessibleLocations };
      }

      // User has no assigned locations - return empty array
      return { success: true, data: [] };
    } catch (error: any) {
      console.error('Error getting accessible locations:', error);
      return { success: false, error: error.message };
    }
  },

  // Get a single location by ID
  getLocation: async (id: string) => {
    try {
      const locationRef = doc(db, 'locations', id);
      const docSnap = await getDoc(locationRef);
      if (docSnap.exists()) {
        return { success: true, data: { id: docSnap.id, ...docSnap.data() } as Location };
      } else {
        return { success: false, error: 'Location not found' };
      }
    } catch (error: any) {
      console.error('Error getting location:', error);
      return { success: false, error: error.message };
    }
  },

  // Add a new location
  addLocation: async (
    locationData: Omit<Location, 'id'>, 
    organizationId?: string,
    userInfo?: { id: string; name: string; email: string }
  ) => {
    try {
      const locationsRef = collection(db, 'locations');
      // Trim whitespace from location name before saving
      const trimmedLocationData = {
        ...locationData,
        name: locationData.name?.trim() || '',
      };
      const docRef = await addDoc(locationsRef, {
        ...trimmedLocationData,
        ...(organizationId && { organizationId }), // Add organizationId if provided
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      
      // Log audit entry
      if (userInfo && organizationId) {
        await auditLogService.log({
          action: 'location_added',
          entityType: 'location',
          entityId: docRef.id,
          entityName: trimmedLocationData.name,
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          organizationId: organizationId,
        });
      }
      
      return { success: true, id: docRef.id };
    } catch (error: any) {
      console.error('Error adding location:', error);
      return { success: false, error: error.message };
    }
  },

  // Update an existing location
  updateLocation: async (
    id: string, 
    locationData: Partial<Location>,
    userInfo?: { id: string; name: string; email: string },
    organizationId?: string
  ) => {
    try {
      // Get current location for audit log
      let currentLocation: Location | null = null;
      if (userInfo && organizationId) {
        const currentResult = await locationService.getLocation(id);
        if (currentResult.success && currentResult.data) {
          currentLocation = currentResult.data;
        }
      }
      
      const locationRef = doc(db, 'locations', id);
      // Trim whitespace from location name if provided
      const trimmedLocationData = {
        ...locationData,
        ...(locationData.name && { name: locationData.name.trim() }),
      };
      await updateDoc(locationRef, {
        ...trimmedLocationData,
        updatedAt: new Date(),
      });
      
      // Log audit entry (only if not updating assignedTemplates - that's handled separately)
      if (userInfo && organizationId && currentLocation && !locationData.assignedTemplates) {
        await auditLogService.log({
          action: 'location_updated',
          entityType: 'location',
          entityId: id,
          entityName: locationData.name || currentLocation.name,
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          organizationId: organizationId,
          details: {
            oldValue: currentLocation,
            newValue: { ...currentLocation, ...trimmedLocationData },
          },
        });
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Error updating location:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete all tasks for a location
  deleteTasksForLocation: async (locationId: string, organizationId?: string) => {
    try {
      const tasksRef = collection(db, 'tasks');
      let tasksQuery;
      
      if (organizationId) {
        // Query tasks by both locationId and organizationId
        tasksQuery = query(
          tasksRef,
          where('locationId', '==', locationId),
          where('organizationId', '==', organizationId)
        );
      } else {
        // Query tasks by locationId only
        tasksQuery = query(
          tasksRef,
          where('locationId', '==', locationId)
        );
      }
      
      const tasksSnapshot = await getDocs(tasksQuery);
      const deletePromises: Promise<void>[] = [];
      
      tasksSnapshot.forEach((taskDoc) => {
        deletePromises.push(deleteDoc(doc(db, 'tasks', taskDoc.id)));
      });
      
      await Promise.all(deletePromises);
      console.log(`✅ Deleted ${tasksSnapshot.size} tasks for location ${locationId}`);
      
      return { success: true, deletedCount: tasksSnapshot.size };
    } catch (error: any) {
      console.error('Error deleting tasks for location:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete a location (soft delete)
  deleteLocation: async (
    id: string,
    userInfo?: { id: string; name: string; email: string },
    organizationId?: string
  ) => {
    try {
      // Get location info before deletion for audit log
      let locationName = 'Unknown';
      if (userInfo && organizationId) {
        const locationResult = await locationService.getLocation(id);
        if (locationResult.success && locationResult.data) {
          locationName = locationResult.data.name;
        }
      }
      
      // Delete all tasks for this location first
      const deleteTasksResult = await locationService.deleteTasksForLocation(id, organizationId);
      if (!deleteTasksResult.success) {
        console.warn(`Warning: Failed to delete some tasks for location ${id}:`, deleteTasksResult.error);
        // Continue with location deletion even if task deletion fails
      } else {
        console.log(`✅ Deleted ${deleteTasksResult.deletedCount || 0} tasks for location ${id}`);
      }
      
      // Remove location from users' locationIds arrays
      if (organizationId) {
        try {
          const usersRef = collection(db, 'users');
          
          // Query 1: Users with organizationId matching current organization (legacy support)
          const orgIdQuery = query(usersRef, where('organizationId', '==', organizationId));
          
          // Query 2: Users with organizationIds array containing current organization
          const orgIdsQuery = query(usersRef, where('organizationIds', 'array-contains', organizationId));
          
          // Execute both queries in parallel
          const [orgIdSnapshot, orgIdsSnapshot] = await Promise.all([
            getDocs(orgIdQuery),
            getDocs(orgIdsQuery)
          ]);
          
          // Combine results and deduplicate by user ID
          const usersMap = new Map<string, { id: string; locationIds: string[] }>();
          
          // Process users from organizationId query
          orgIdSnapshot.forEach((userDoc) => {
            const userData = userDoc.data();
            const locationIds = userData.locationIds || [];
            if (locationIds.includes(id)) {
              usersMap.set(userDoc.id, {
                id: userDoc.id,
                locationIds: locationIds
              });
            }
          });
          
          // Process users from organizationIds array query (skip duplicates)
          orgIdsSnapshot.forEach((userDoc) => {
            if (usersMap.has(userDoc.id)) return; // Skip if already added
            const userData = userDoc.data();
            const locationIds = userData.locationIds || [];
            if (locationIds.includes(id)) {
              usersMap.set(userDoc.id, {
                id: userDoc.id,
                locationIds: locationIds
              });
            }
          });
          
          // Update all users that have this location in their locationIds
          const updatePromises: Promise<void>[] = [];
          usersMap.forEach((userInfo) => {
            const updatedLocationIds = userInfo.locationIds.filter((locId: string) => locId !== id);
            updatePromises.push(
              updateDoc(doc(db, 'users', userInfo.id), {
                locationIds: updatedLocationIds,
                updatedAt: new Date(),
              })
            );
          });
          
          await Promise.all(updatePromises);
          console.log(`✅ Removed location ${id} from ${updatePromises.length} users`);
        } catch (error: any) {
          console.warn(`Warning: Failed to remove location from users:`, error);
          // Continue with location deletion even if user update fails
        }
      }
      
      const locationRef = doc(db, 'locations', id);
      await updateDoc(locationRef, {
        deleted: true,
        updatedAt: new Date(),
      });
      
      // Log audit entry
      if (userInfo && organizationId) {
        await auditLogService.log({
          action: 'location_deleted',
          entityType: 'location',
          entityId: id,
          entityName: locationName,
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          organizationId: organizationId,
          details: {
            deletedTasksCount: deleteTasksResult.deletedCount || 0,
          },
        });
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Error deleting location:', error);
      return { success: false, error: error.message };
    }
  },

  // Copy a location
  copyLocation: async (id: string) => {
    try {
      const locationRef = doc(db, 'locations', id);
      const docSnap = await getDoc(locationRef);
      if (docSnap.exists()) {
        const originalData = docSnap.data() as Location;
        const newLocationData: Omit<Location, 'id'> = {
          ...originalData,
          name: `${originalData.name} (Copy)`,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        const locationsRef = collection(db, 'locations');
        const newDocRef = await addDoc(locationsRef, newLocationData);
        return { success: true, id: newDocRef.id };
      } else {
        return { success: false, error: 'Location not found' };
      }
    } catch (error: any) {
      console.error('Error copying location:', error);
      return { success: false, error: error.message };
    }
  },
};

